/**
 * InterventionNotification - Real-time intervention notifications
 * Integrates WebSocket events with notification store
 */

import React, { useEffect } from 'react';
import { useWebSocket } from '../realtime/WebSocketProvider';
import { useNotificationStore } from '@/stores/notification';
import { useAuthStore } from '@/stores/auth';

export const InterventionNotification: React.FC = () => {
  const { subscribeToMessage } = useWebSocket();
  const { user } = useAuthStore();
  const { addNotification, addToPopupQueue, preferences } = useNotificationStore();

  useEffect(() => {
    if (!user) return;

    const unsubscribeRequired = subscribeToMessage('intervention_required', (data: any) => {
      const notification = {
        type: 'intervention_required' as const,
        title: 'Intervention Required',
        message: data.data?.prompt || data.interventionData?.description || 'Intervention required',
        priority: (data.data?.urgency || data.interventionData?.priority || 'MEDIUM') as any,
        interventionId: data.interventionId,
        projectId: data.projectId,
        interventionType: data.data?.interventionType || data.interventionData?.type,
      };

      const newNotification = addNotification(notification);
      
      // Add to popup queue if in-app notifications enabled
      if (preferences.inAppNotifications) {
        addToPopupQueue(newNotification);
      }
    });

    const unsubscribeAssigned = subscribeToMessage('intervention_assigned', (data: any) => {
      // Only show if assigned to current user
      if (data.data?.assignedTo !== user.id) return;

      const notification = {
        type: 'intervention_assigned' as const,
        title: 'Intervention Assigned',
        message: data.data?.description || 'You have been assigned an intervention',
        priority: (data.data?.priority || 'MEDIUM') as any,
        interventionId: data.interventionId,
        projectId: data.projectId,
        interventionType: data.data?.interventionType,
        requestingAgent: data.data?.requestingAgent,
      };

      const newNotification = addNotification(notification);
      
      // Add to popup queue if in-app notifications enabled
      if (preferences.inAppNotifications) {
        addToPopupQueue(newNotification);
      }
    });

    const unsubscribeEscalated = subscribeToMessage('intervention_escalated', (data: any) => {
      // Only show if escalated to current user
      if (data.data?.escalatedTo !== user.id) return;

      const notification = {
        type: 'intervention_escalated' as const,
        title: '?? Intervention Escalated',
        message: `${data.data?.reason || 'An intervention has been escalated to you'}`,
        priority: 'CRITICAL' as const,
        interventionId: data.interventionId,
        projectId: data.projectId,
        interventionType: data.data?.interventionType,
      };

      const newNotification = addNotification(notification);
      
      // Always add escalated notifications to popup queue
      if (preferences.inAppNotifications) {
        addToPopupQueue(newNotification);
      }
    });

    const unsubscribeResolved = subscribeToMessage('intervention_resolved', () => {});

    // Cleanup subscriptions
    return () => {
      unsubscribeRequired();
      unsubscribeAssigned();
      unsubscribeEscalated();
      unsubscribeResolved();
    };
  }, [subscribeToMessage, user, addNotification, addToPopupQueue, preferences.inAppNotifications]);

  // Component doesn't render anything visible
  return null;
};

export default InterventionNotification;